/**
 * NiV tool — inbound system notifications written by the app for the admin
 * (e.g. a therapist typed a custom sub-specialization). Read + delete.
 */

import { useState } from "react";
import {
  BellRinging,
  Buildings,
  EnvelopeSimple,
  Tag,
  Trash,
  User,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import {
  useGetSystemNotificationsQuery,
  useDeleteSystemNotificationMutation,
} from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import { formatNivDateTime } from "./NivSystemMessagesTool";

const TYPE_LABELS = { custom_sub_specialization: "תת-התמחות מותאמת" };

function NivSystemUpdatesTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const { data: notifications = [], isLoading } = useGetSystemNotificationsQuery(undefined, {
    skip: !isAuthorized,
  });
  const [deleteNotif, { isLoading: deleting }] = useDeleteSystemNotificationMutation();
  const [deleteId, setDeleteId] = useState(null);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteNotif({ notifId: deleteId }).unwrap();
      showSuccess("ההודעה נמחקה");
    } catch {
      showError("שגיאה במחיקת ההודעה");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="niv-system-updates-content">
      <div className="niv-system-updates-header">
        <h3>
          <BellRinging size={22} weight="fill" />
          עדכוני מערכת
        </h3>
        <span className="niv-system-updates-count">{notifications.length} הודעות</span>
      </div>

      {isLoading ? (
        <ContentLoader message="טוען הודעות..." />
      ) : notifications.length === 0 ? (
        <div className="niv-empty-state">
          <BellRinging size={48} weight="light" />
          <p>אין עדכוני מערכת</p>
        </div>
      ) : (
        <div className="niv-system-updates-list">
          {notifications.map((notif) => (
            <div key={notif.id} className="niv-system-update-card">
              <div className="niv-system-update-header">
                <span className="niv-system-update-date">{formatNivDateTime(notif.createdAt)}</span>
                <span className={`niv-system-update-type niv-system-update-type--${notif.type || "info"}`}>
                  {TYPE_LABELS[notif.type] || notif.type || "מידע"}
                </span>
              </div>
              <div className="niv-system-update-body">
                <p className="niv-system-update-message">{notif.message}</p>
                <div className="niv-system-update-meta">
                  {notif.therapistName && (
                    <span className="niv-system-update-meta-item">
                      <User size={14} /> {notif.therapistName}
                    </span>
                  )}
                  {notif.therapistEmail && (
                    <span className="niv-system-update-meta-item">
                      <EnvelopeSimple size={14} /> {notif.therapistEmail}
                    </span>
                  )}
                  {notif.clinicName && (
                    <span className="niv-system-update-meta-item">
                      <Buildings size={14} /> {notif.clinicName}
                    </span>
                  )}
                  {notif.clinicTypeLabel && (
                    <span className="niv-system-update-meta-item">
                      <Tag size={14} /> {notif.clinicTypeLabel}
                    </span>
                  )}
                  {notif.customValue && (
                    <span className="niv-system-update-meta-item niv-system-update-highlight">
                      &ldquo;{notif.customValue}&rdquo;
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="niv-system-update-delete"
                onClick={() => setDeleteId(notif.id)}
                disabled={deleting}
                title="מחק הודעה"
                aria-label="מחק הודעה"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="מחיקת עדכון"
        message="העדכון יימחק לצמיתות. האם להמשיך?"
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default NivSystemUpdatesTool;
